import * as React from "react";
import type { SAStrategyPillar, SASectionCopy } from "@/data/sellerAnalysisTypes";
import { Reveal, Overline } from "@/components/presentation/primitives";

interface Props {
  strategy: SAStrategyPillar[];
  intro?: SASectionCopy;
}

export const SA_Strategy: React.FC<Props> = ({ strategy, intro }) => {
  if (!strategy?.length) return null;

  return (
    <section
      className="w-full bg-ivory-alt py-20 md:py-28"
      data-testid="sa-strategy"
      aria-label="How I would position the property"
    >
      <div className="mx-auto w-full max-w-5xl px-6 md:px-10">
        <Reveal>
          <Overline>{intro?.overline || "How I'd position it"}</Overline>
          <h2 className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-charcoal md:text-4xl lg:text-5xl">
            {intro?.headline || "If this were my listing, here's how I'd position it."}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-x-14 gap-y-12 md:grid-cols-2">
          {strategy.map((p, i) => (
            <Reveal key={p.key} delay={(i % 2) * 0.1}>
              <div className="border-t border-charcoal/15 pt-6" data-testid={`pillar-${p.key}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-2xl text-brass md:text-3xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-sm font-medium uppercase tracking-[0.2em] text-charcoal">
                    {p.label}
                  </h3>
                </div>
                <p className="mt-4 max-w-prose text-base leading-relaxed text-charcoal-soft md:text-lg">
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
